interface CourseOverviewProps {
    course: any;
    topics: any[];
    deleteCourse: () => void;
    deleting: boolean;
    description: string;
    setDescription: (desc: string) => void;
    saveDescription: () => void;
    learningGoals: string[];
    editingGoals: string[];
    isEditingGoals: boolean;
    loadingGoals: boolean;
    generateLearningGoals: () => void;
    startEditGoals: () => void;
    cancelEditGoals: () => void;
    saveGoals: () => void;
    handleGoalChange: (index: number, value: string) => void;
    addGoal: () => void;
    removeGoal: (index: number) => void;
    showNotAvailablePopup: (feature: string) => void;
    setActiveTopicId: (id: string | null) => void;
}

export default function CourseOverview({
                                           course,
                                           topics,
                                           deleteCourse,
                                           deleting,
                                           description,
                                           setDescription,
                                           saveDescription,
                                           learningGoals,
                                           editingGoals,
                                           isEditingGoals,
                                           loadingGoals,
                                           generateLearningGoals,
                                           startEditGoals,
                                           cancelEditGoals,
                                           saveGoals,
                                           handleGoalChange,
                                           addGoal,
                                           removeGoal,
                                           showNotAvailablePopup,
                                           setActiveTopicId
                                       }: CourseOverviewProps) {
    return (
        <>
            {/* Header */}
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col gap-3">
                    <div className="flex-1">
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 text-gray-800">
                            {course.title}
                        </h1>
                        <div className="flex flex-wrap gap-2 md:gap-3">
                            <span className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                                <span>📚</span>
                                {topics.length} Thema{topics.length !== 1 ? 'en' : ''}
                            </span>
                            {course.grade && (
                                <span className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                                    <span>🎓</span>
                                    Klasse {course.grade}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={deleteCourse}
                            disabled={deleting}
                            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-red-500/20 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                        >
                            {deleting ? (
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Löschen...
                                </span>
                            ) : '🗑 Löschen'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
                {/* Description Card */}
                <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg hover:border-green-500/30">
                    <div className="p-4 md:p-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800">
                            <span>📝</span>
                            Kursbeschreibung
                        </h3>
                        <button
                            onClick={saveDescription}
                            className="w-full sm:w-auto px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-blue-500/20 text-sm"
                        >
                            💾 Speichern
                        </button>
                    </div>
                    <div className="p-4 md:p-5">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Beschreibe hier den Kursinhalt, Ziele und Anforderungen..."
                            rows={3}
                            className="w-full px-3 py-2 text-sm md:text-base bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-y transition-all"
                        />
                        <div className="text-right text-gray-500 text-xs md:text-sm mt-2">
                            {description.length} Zeichen
                        </div>
                    </div>
                </div>

                {/* Learning Goals Card */}
                <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg hover:border-green-500/30">
                    <div className="p-4 md:p-5 border-b border-gray-200 flex flex-col gap-3">
                        <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800">
                            <span>🎯</span>
                            Lernziele
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {!isEditingGoals ? (
                                <>
                                    <button
                                        onClick={generateLearningGoals}
                                        disabled={loadingGoals}
                                        className="flex-1 min-w-[140px] px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-green-500/20 disabled:opacity-60 text-sm"
                                    >
                                        {loadingGoals ? (
                                            <span className="flex items-center gap-2">
                                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                Generiere...
                                            </span>
                                        ) : '🤖 KI generieren'}
                                    </button>
                                    <button
                                        onClick={startEditGoals}
                                        className="flex-1 min-w-[120px] px-3 py-2 rounded-lg bg-gray-200 text-gray-800 font-medium cursor-pointer transition-all hover:bg-gray-300 text-sm"
                                    >
                                        ✏️ Bearbeiten
                                    </button>
                                </>
                            ) : (
                                <div className="flex flex-wrap gap-2 w-full">
                                    <button
                                        onClick={saveGoals}
                                        className="flex-1 min-w-[100px] px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-blue-500/20 text-sm"
                                    >
                                        💾 Speichern
                                    </button>
                                    <button
                                        onClick={cancelEditGoals}
                                        className="flex-1 min-w-[100px] px-3 py-2 rounded-lg bg-gray-400 text-white font-medium cursor-pointer transition-all hover:bg-gray-500 text-sm"
                                    >
                                        ❌ Abbrechen
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-4 md:p-5">
                        {!isEditingGoals ? (
                            learningGoals?.length > 0 ? (
                                <ul className="space-y-2">
                                    {learningGoals.map((goal: string, i: number) => (
                                        <li key={i} className="flex items-start gap-2 p-2 md:p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-500/30 transition-all">
                                            <div className="text-base mt-0.5">🎯</div>
                                            <span className="text-gray-700 text-sm">{goal}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-4 md:py-6 text-gray-500">
                                    <div className="text-3xl mb-2 opacity-50">🎯</div>
                                    <p className="font-medium text-sm">Noch keine Lernziele definiert</p>
                                    <small className="text-xs">Nutze KI-Generierung oder füge manuell hinzu</small>
                                </div>
                            )
                        ) : (
                            <div className="space-y-2">
                                {editingGoals.map((goal, i) => (
                                    <div key={i} className="flex gap-2 items-center">
                                        <input
                                            type="text"
                                            value={goal}
                                            onChange={(e) => handleGoalChange(i, e.target.value)}
                                            className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                                            placeholder="Lernziel eingeben..."
                                        />
                                        <button
                                            onClick={() => removeGoal(i)}
                                            disabled={editingGoals.length <= 1}
                                            className="px-2.5 py-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-bold cursor-pointer transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={addGoal}
                                    className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-purple-500/20 text-sm"
                                >
                                    ➕ Lernziel hinzufügen
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg hover:border-green-500/30">
                <div className="p-4 md:p-5 border-b border-gray-200">
                    <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800">
                        <span>⚡</span>
                        Schnellaktionen
                    </h3>
                </div>
                <div className="p-4 md:p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                            onClick={() => topics.length > 0 && setActiveTopicId(topics[0].id)}
                            disabled={topics.length === 0}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 cursor-pointer transition-all hover:bg-gray-100 hover:border-green-500 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-xl">📂</span>
                            <span className="font-medium text-sm">Thema öffnen</span>
                        </button>
                        <button
                            onClick={() => showNotAvailablePopup("Teilnehmer einladen")}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 cursor-pointer transition-all hover:bg-gray-100 hover:border-green-500 hover:-translate-y-1"
                        >
                            <span className="text-xl">👥</span>
                            <span className="font-medium text-sm">Teilnehmer einladen</span>
                        </button>
                        <button
                            onClick={() => showNotAvailablePopup("Kurs teilen")}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 cursor-pointer transition-all hover:bg-gray-100 hover:border-green-500 hover:-translate-y-1"
                        >
                            <span className="text-xl">🔗</span>
                            <span className="font-medium text-sm">Teilen</span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}